import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { AuthClient } from "@nav-games/auth-client";
import { ICS_FLAGS } from "@nav-games/flags";
import {
  type Attempt,
  type Difficulty,
  type SessionResult,
  SessionRecorder,
  isPremium,
  recordAttempt,
  pickWeightedKey,
  statsFor,
} from "@nav-games/games-core";

import { Flag } from "../../ui/Flag";
import { HelpButton, HelpPanel, InlineReference } from "../../ui/HelpPanel";
import { PremiumGate } from "../../ui/PremiumGate";

interface FlashcardsProps {
  auth: AuthClient;
  onNavigate: (to: string) => void;
}

const MODULE = "flags";
const GAME = "flashcards";
const CARDS_PER_SESSION = 20;
const BEAT_THE_CLOCK_MS = 60_000;
const CHOICES_PER_CARD = 6;

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const DIGITS = "0123456789".split("");

type SessionMode = "cards" | "timed";

function poolFor(difficulty: Difficulty): readonly string[] {
  switch (difficulty) {
    case "beginner":
      return LETTERS;
    case "intermediate":
    case "expert":
      return [...LETTERS, ...DIGITS];
  }
}

interface Card {
  answer: string;
  choices: readonly string[];
}

function nextCard(pool: readonly string[], recent: readonly string[]): Card {
  const answer = pickWeightedKey(MODULE, pool, recent);
  const distractors = new Set<string>();
  while (distractors.size < CHOICES_PER_CARD - 1) {
    const d = pool[Math.floor(Math.random() * pool.length)]!;
    if (d !== answer) distractors.add(d);
  }
  const choices = shuffle([answer, ...distractors]);
  return { answer, choices };
}

function shuffle<T>(input: readonly T[]): T[] {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const ai = arr[i]!;
    const aj = arr[j]!;
    arr[i] = aj;
    arr[j] = ai;
  }
  return arr;
}

interface Settings {
  difficulty: Difficulty;
  sessionMode: SessionMode;
}

export function Flashcards({ auth, onNavigate }: FlashcardsProps) {
  const [settings, setSettings] = useState<Settings | null>(null);
  const premium = isPremium();

  if (!settings) {
    return (
      <Picker
        auth={auth}
        premium={premium}
        onStart={setSettings}
        onBack={() => onNavigate("/play/flags")}
      />
    );
  }
  return (
    <Game
      settings={settings}
      onAgain={() => setSettings({ ...settings })}
      onBack={() => onNavigate("/play/flags")}
    />
  );
}

function Picker({
  auth,
  premium,
  onStart,
  onBack,
}: {
  auth: AuthClient;
  premium: boolean;
  onStart: (s: Settings) => void;
  onBack: () => void;
}) {
  const [difficulty, setDifficulty] = useState<Difficulty>("beginner");
  const [sessionMode, setSessionMode] = useState<SessionMode>("cards");
  return (
    <>
      <h2>Flag flashcards</h2>
      <div className="card">
        <h3>Level</h3>
        <div
          className="row"
          style={{ flexDirection: "column", gap: 8, alignItems: "stretch" }}
        >
          <button
            className={difficulty === "beginner" ? "" : "secondary"}
            onClick={() => setDifficulty("beginner")}
          >
            Beginner — letters only (A–Z)
          </button>
          <button
            className={difficulty === "intermediate" ? "" : "secondary"}
            disabled={!premium}
            onClick={() => premium && setDifficulty("intermediate")}
          >
            Intermediate — letters + digits {!premium && "🔒"}
          </button>
        </div>
      </div>
      <div className="card">
        <h3>Session length</h3>
        <div
          className="row"
          style={{ flexDirection: "column", gap: 8, alignItems: "stretch" }}
        >
          <button
            className={sessionMode === "cards" ? "" : "secondary"}
            onClick={() => setSessionMode("cards")}
          >
            {CARDS_PER_SESSION} cards
          </button>
          <button
            className={sessionMode === "timed" ? "" : "secondary"}
            onClick={() => setSessionMode("timed")}
          >
            Beat the clock — {Math.round(BEAT_THE_CLOCK_MS / 1000)} seconds
          </button>
        </div>
      </div>
      {!premium && <PremiumGate auth={auth} />}
      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        <button onClick={() => onStart({ difficulty, sessionMode })}>Start</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Flags
        </button>
      </div>
    </>
  );
}

function Game({
  settings,
  onAgain,
  onBack,
}: {
  settings: Settings;
  onAgain: () => void;
  onBack: () => void;
}) {
  const { difficulty, sessionMode } = settings;
  const pool = useMemo(() => poolFor(difficulty), [difficulty]);
  // Recent answers kept out of the next draw so the spaced-repetition
  // picker doesn't resurface the same card within a short window.
  // Clamped to pool size - 1 so there is always an eligible card.
  const avoidWindow = Math.min(8, pool.length - 1);
  const recentRef = useRef<string[]>([]);
  const recorder = useMemo(
    () =>
      new SessionRecorder({
        module: MODULE,
        game: GAME,
        length: sessionMode === "cards" ? CARDS_PER_SESSION : 0,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settings],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => {
    const c = nextCard(pool, []);
    recentRef.current = [c.answer];
    return c;
  });
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [feedback, setFeedback] = useState<"right" | "wrong" | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef<number>(Date.now());

  const [now, setNow] = useState<number>(Date.now());
  useEffect(() => {
    if (sessionMode !== "timed" || result) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [sessionMode, result]);
  const timeLeftMs = Math.max(
    0,
    BEAT_THE_CLOCK_MS - (now - sessionStartedAt.current),
  );
  useEffect(() => {
    if (sessionMode !== "timed" || result) return;
    if (timeLeftMs === 0) setResult(recorder.summarise());
  }, [timeLeftMs, sessionMode, result, recorder]);

  const advance = useCallback(() => {
    if (sessionMode === "cards" && index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    const next = nextCard(pool, recentRef.current);
    recentRef.current = [...recentRef.current, next.answer].slice(-avoidWindow);
    setCard(next);
    setShownAt(Date.now());
    setFeedback(null);
  }, [avoidWindow, index, pool, recorder, sessionMode]);

  const onChoose = useCallback(
    (choice: string) => {
      if (feedback || result) return;
      const ms = Date.now() - shownAt;
      const ok = choice === card.answer;
      const attempt: Omit<Attempt, "module"> = {
        char: card.answer,
        ok,
        ms,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setFeedback(ok ? "right" : "wrong");
      setTimeout(advance, ok ? 400 : 900);
    },
    [advance, card, feedback, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        pool={pool}
        difficulty={difficulty}
        onAgain={onAgain}
        onBack={onBack}
      />
    );
  }

  const liveCorrect = recorder.summarise().correct;
  const header =
    sessionMode === "cards"
      ? `Card ${index + 1} of ${CARDS_PER_SESSION} · ${difficulty}`
      : `${Math.ceil(timeLeftMs / 1000)} s left · ${liveCorrect} right · ${difficulty}`;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "flags" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted">{header}</p>
      <div className="card" style={{ textAlign: "center" }}>
        <Flag char={card.answer} size={180} />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 8,
            marginTop: 16,
          }}
        >
          {card.choices.map((c) => (
            <button
              key={c}
              className="secondary"
              disabled={!!feedback}
              onClick={() => onChoose(c)}
              style={{
                fontSize: 20,
                padding: "12px 0",
                background:
                  feedback && c === card.answer
                    ? "rgba(44, 122, 59, 0.18)"
                    : undefined,
              }}
            >
              {c}
            </button>
          ))}
        </div>
      </div>
      {difficulty === "beginner" && <InlineReference section="flags" />}
    </>
  );
}

function EndCard({
  result,
  pool,
  difficulty,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  pool: readonly string[];
  difficulty: Difficulty;
  onAgain: () => void;
  onBack: () => void;
}) {
  const rolling = pool
    .map((c) => ({ char: c, ...statsFor(MODULE, c) }))
    .filter((s) => s.attempts >= 3 && s.hitRate < 1)
    .sort((a, b) => a.hitRate - b.hitRate);
  const weakest = rolling.slice(0, 5);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}.
        </p>
        {result.total > 0 && (
          <p className="small muted">
            Mean response: {Math.round(result.meanMs)} ms.
          </p>
        )}
      </div>
      {weakest.length > 0 && (
        <div className="card">
          <h3>Weakest flags (rolling)</h3>
          <ul className="small muted">
            {weakest.map((w) => (
              <li key={w.char}>
                <code>{w.char}</code>
                {ICS_FLAGS[w.char]?.name ? ` — ${ICS_FLAGS[w.char]!.name}` : ""} —
                {" "}
                {Math.round(w.hitRate * 100)}% over {w.attempts} attempts
              </li>
            ))}
          </ul>
        </div>
      )}
      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        <button onClick={onAgain}>Play another ({difficulty})</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Flags
        </button>
      </div>
    </>
  );
}
